import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetRoomsForExportQuery} from '@/services/graphqlHooks';
import {useGetRoomsForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {ExportDefinition} from '../../types/exportTypes';
import {formatPersonName, noOptions} from '../exportOptions';

type RoomExportRecord = GetRoomsForExportQuery['getRooms'][number] & {
  residentNames: string;
};

export const roomsExport: ExportDefinition<
  Record<string, never>,
  RoomExportRecord
> = {
  id: 'rooms',
  label: i18n.t('Rooms'),
  permission: 'EXPORT_ROOMS',
  entitlement: 'ROOMS',
  ...noOptions,
  load: async () => {
    const {getRooms, getAllCurrentResidents} = await queryClient.fetchQuery({
      queryKey: useGetRoomsForExportQuery.getKey(),
      queryFn: useGetRoomsForExportQuery.fetcher(graphqlClient),
    });
    const residentNames = new Map(
      getAllCurrentResidents.map(resident => [
        resident.id,
        formatPersonName(resident.name),
      ])
    );

    return getRooms.map(room => ({
      ...room,
      residentNames: room.residents
        .flatMap(id => (id && residentNames.get(id)) || [])
        .join(', '),
    }));
  },
  columns: [
    {header: i18n.t('Room number'), value: room => room.roomNumber},
    {
      header: i18n.t('Building'),
      filterable: true,
      value: room => room.building,
    },
    {header: i18n.t('Floor'), filterable: true, value: room => room.floor},
    {header: i18n.t('Capacity'), value: room => room.capacity},
    {header: i18n.t('Occupied beds'), value: room => room.residents.length},
    {header: i18n.t('Residents'), value: room => room.residentNames},
  ],
  getFileName: () => 'rooms',
};
